'use client';

import Link from 'next/link';
import React, { useState, useReducer } from 'react';
import IconColour from '../svg/icon_colour';
import LogoWithoutPayoffColour from '../svg/logo_without_payoff_colour';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { trackEvent } from '../google-tag-manager';
import { usePathname } from 'next/navigation';

const featureLinks = [{
  href: '/digital-consultation-forms',
  label: 'Digital Consultation Forms',
  description: 'Compliant and automated',
}, {
  href: '/online-booking',
  label: 'Online Booking',
  description: 'Booking deposits, smart time slots, and cancellation policies',
}, {
  href: '/ratings-and-reviews',
  label: 'Ratings & Reviews',
  description: 'Ratings & Reviews',
}, {
  href: '/salon-marketing-software-system',
  label: 'Salon Marketing',
  description: 'Smart campaigns, templates, and custom filters',
}, {
  href: '/reports',
  label: 'Reports & Dashboards',
  description: 'Powerful salon business reporting to provide you with key insights required to track performance, increase revenue and get time back.',
}, {
  href: '/stock-management',
  label: 'Stock Management',
  description: 'Stock Management',
}];

interface FullScreenNavProps {
  onClose: () => void;
  cta?: React.ReactNode;
  featuredPages: Array<{
    href: string;
    label: string;
  }>;
}

const FullScreenNav = (props: FullScreenNavProps) => {
  const { onClose, cta, featuredPages } = props;

  const [expanded, toggleExpanded] = useReducer((ex: boolean) => !ex, false);
  const pathname = usePathname();

  const content = (
    <div className="flex flex-col min-h-full">
      <div className="flex justify-between sticky top-0 bg-white">
        <div className="p-4">
          <Link href="/" className="flex items-center gap-2" onClick={onClose}>
            <div className="h-9">
              <IconColour />
            </div>
            <div className="h-6 mt-1">
              <LogoWithoutPayoffColour />
            </div>
          </Link>
        </div>

        <div className="p-3">
          <button aria-label="Close navigation" type="button" className="btn btn-circle btn-ghost" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} size="2x" />
          </button>
        </div>
      </div>

      <div>
        <div>
          <button type="button" className="w-full px-8 text-3xl" onClick={toggleExpanded}>
            <div className={`font-bold ${expanded ? '' : 'border-b-2 border-gray-100'} pb-4 py-5 flex items-center justify-between`}>
              Features
              {expanded ? (
                <FontAwesomeIcon icon={faChevronUp} size="xs" />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} size="xs" />
              )}
            </div>
          </button>

          {expanded ? (
            <div className="bg-gray-50">
              <ul className="py-2">
                {featuredPages.map((fl) => (
                  <li key={fl.href}>
                    <Link href={fl.href} className="block px-8 py-2 text-xl link link-hover" onClick={onClose}>
                      {fl.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div>
          <Link href="/pricing" className="block px-8 text-3xl" onClick={onClose}>
            <div className="font-bold border-b-2 border-gray-100 pb-4 py-5">
              Pricing
            </div>
          </Link>
        </div>

        <div>
          <Link href="/blog" className="block px-8 text-3xl" onClick={onClose}>
            <div className="font-bold border-b-2 border-gray-100 pb-4 py-5">
              Blog
            </div>
          </Link>
        </div>

        <div>
          <a href="https://diary.thesalon.app/login" target="_self" className="block px-8 text-3xl">
            <div className="font-bold border-b-2 border-gray-100 pb-4 py-5">
              Log in
            </div>
          </a>
        </div>
      </div>

      <div className="flex-grow" />

      <div className="p-8">
        {cta ? cta : (() => {
          if (pathname === '/book-demo') { return null; }

          return (
            <Link href="/book-demo?open=true" as="/book-demo" className="btn btn-primary rounded-full text-base btn-block" onClick={() => {
              trackEvent({
                event: 'book_demo_click',
              });

              onClose();
            }}>
              Book demo
            </Link>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 bottom-0 z-10 bg-black/50">
      <div className="bg-white h-full w-full max-w-md ml-auto overflow-auto">
        {content}
      </div>
    </div>
  );
};

interface MainNavProps {
  cta?: React.ReactNode;
  mobileCta?: React.ReactNode;
  featuredPages?: Array<{
    href: string;
    label: string;
  }>;
}

const MainNav: React.FC<MainNavProps> = (props) => {
  const { cta, mobileCta, featuredPages = featureLinks } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const pathname = usePathname();

  const isContactSalesPage = pathname === '/contact-sales';
  const isBookDemoPage = pathname === '/book-demo';

  return (
    <>
      <header>
        <div className="bg-slate-50 py-1">
          <div className={`container mx-auto max-w-screen-xl ${isBookDemoPage ? 'px-8' : 'px-1'} sm:px-8`}>
            <div className="flex gap-4 sm:gap-6 justify-end items-center h-7">
              <div>
                <a
                  href="https://diary.thesalon.app/login"
                  target="_self"
                  className="link link-hover text-xs md:text-sm font-medium"
                >
                  Log in
                </a>
              </div>

              {isContactSalesPage ? null : (
                <div>
                  <Link href="/contact-sales" className="link link-hover text-xs md:text-sm font-medium">
                    Contact Sales
                  </Link>
                </div>
              )}

              {isBookDemoPage ? null : (
                <div className="sm:hidden">
                  <Link href="/book-demo" className="btn btn-xs btn-primary rounded-full">
                    Book demo
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="container mx-auto max-w-screen-xl px-8 py-4">
          <div className="flex items-center gap-4">
            <div>
              <Link href="/" className="flex items-center gap-2">
                <div className="h-9">
                  <IconColour />
                </div>
                <div className="h-6 mt-1">
                  <LogoWithoutPayoffColour />
                </div>
              </Link>
            </div>

            <div className="px-20 hidden lg:block">
              <nav>
                <ul className="flex gap-8">
                  <li>
                    <div className="relative" onMouseEnter={() => setExpanded(true)} onMouseLeave={() => setExpanded(false)}>
                      <button type="button" className="font-bold p-2" onClick={() => setExpanded(!expanded)}>
                        Features
                        {expanded ? (
                          <FontAwesomeIcon icon={faChevronUp} size="xs" className="ml-2" />
                        ) : (
                          <FontAwesomeIcon icon={faChevronDown} size="xs" className="ml-2" />
                        )}
                      </button>

                      {expanded ? (
                        <div className="absolute left-0 top-full z-10 pt-2">
                          <div className="bg-white border px-4 py-6 shadow-lg rounded-xl w-96">
                            <ul>
                              {featuredPages.map((fl) => (
                                <li key={fl.href}>
                                  <Link href={fl.href} className="block px-8 py-2 text-xl link link-hover" onClick={() => setExpanded(false)}>
                                    {fl.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </li>
                  <li>
                    <Link href="/pricing" className="block font-bold link link-hover p-2">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link href="/blog" className="block font-bold link link-hover p-2">
                      Blog
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            <div className="flex-grow" />

            <div className="hidden sm:block">
              {cta ? cta : (() => {
                if (isBookDemoPage) { return null; }

                return (
                  <Link href="/book-demo?open=true" as="/book-demo" className="btn btn-sm btn-primary rounded-full h-10 px-6 text-base" onClick={() => {
                    trackEvent({
                      event: 'book_demo_click',
                    });
                  }}>
                    Book demo
                  </Link>
                );
              })()}
            </div>

            <div className="lg:hidden">
              <button aria-label="Open navigation" type="button" className="btn btn-circle btn-ghost" onClick={() => setIsOpen(true)}>
                <FontAwesomeIcon icon={faBars} size="2x" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {isOpen ? (
        <FullScreenNav
          onClose={() => setIsOpen(false)}
          cta={mobileCta}
          featuredPages={featuredPages}
        />
      ) : null}
    </>
  );
};

export default MainNav;
